import { ControlLink, Stack } from '@moda/om';
import classNames from 'classnames';
import React, { useCallback, useRef } from 'react';
import { useMobileNavigator } from '../../../hooks/useNavigator';

import './MobileNavHeader.scss';

interface Props {
  onChange: (index: number) => void;
  selectedTabIndex: number;
}

export const MobileNavHeader: React.FC<Props> = ({ onChange, selectedTabIndex }) => {
  const { data: verticals } = useMobileNavigator();
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      if (!verticals) return;

      const lastIndex = verticals.length - 1;

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          {
            const nextIndex = index === lastIndex ? 0 : index + 1;
            onChange(nextIndex);
            tabRefs.current[nextIndex]?.focus();
          }
          break;
        case 'ArrowLeft':
          event.preventDefault();
          {
            const prevIndex = index === 0 ? lastIndex : index - 1;
            onChange(prevIndex);
            tabRefs.current[prevIndex]?.focus();
          }
          break;
        case 'Home':
          event.preventDefault();
          onChange(0);
          tabRefs.current[0]?.focus();
          break;
        case 'End':
          event.preventDefault();
          onChange(lastIndex);
          tabRefs.current[lastIndex]?.focus();
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onChange(index);
          break;
      }
    },
    [verticals, onChange]
  );

  if (!verticals) return null;

  return (
    <Stack
      className="MobileNavHeader"
      direction="horizontal"
      justifyContent="space-between"
      space={0}
      role="tablist"
      aria-label="Navigation categories"
    >
      {verticals.map(({ title }, index) => {
        const isSelected = selectedTabIndex === index;
        return (
          <div
            ref={el => {
              tabRefs.current[index] = el;
            }}
            className={classNames('MobileNavHeader__tab', {
              'MobileNavHeader__tab--selected': isSelected,
              'MobileNavHeader__tab--two-items': verticals.length === 2,
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              'MobileNavHeader__tab--three-items': verticals.length === 3,
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              'MobileNavHeader__tab--four-items': verticals.length === 4
            })}
            onClick={() => onChange(index)}
            onKeyDown={event => handleKeyDown(event, index)}
            key={`${title} -- ${index}`}
            role="tab"
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
          >
            <ControlLink underlined={isSelected} tabIndex={-1}>
              {title.toUpperCase()}
            </ControlLink>
          </div>
        );
      })}
    </Stack>
  );
};
